import React, { useState, useCallback, useMemo } from "react";
import {
  Card,
  Box,
  Typography,
  Chip,
  Grid,
  ButtonGroup,
  Button,
  LinearProgress,
  IconButton,
  Stack,
  Collapse,
  Divider,
  Link as MuiLink,
} from "@mui/material";
import {
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  Language as LanguageIcon,
  Storage as StorageIcon,
  Devices as DevicesIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Accessibility as AccessibilityIcon,
  Info as InfoIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Speed as SpeedIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { styled } from "@mui/material/styles";

import { defaultTemplateUrl } from "../../utils/index";
import {
  LOCAL_STORAGE_CURRENT_ACCOUNT,
  getLocalStorage,
} from "../../utils/localStorage";
import { PerformanceDetailsModal } from "../SiteDetailsCard/PerformanceDetailsModal";

// Styled components for better reusability
const StatusChip = styled(Chip)(({ theme, status }) => {
  const getStatusColor = () => {
    switch (status) {
      case "active":
      case "production":
        return {
          bgcolor: theme.palette.success.light,
          color: theme.palette.success.dark,
        };
      case "pending":
      case "pending-test":
      case "pending-production":
      case "test":
        return {
          bgcolor: theme.palette.warning.light,
          color: theme.palette.warning.dark,
        };
      default:
        return {
          bgcolor: theme.palette.info.light,
          color: theme.palette.info.dark,
        };
    }
  };

  return {
    ...getStatusColor(),
    fontWeight: "medium",
    textTransform: "capitalize",
    "& .MuiChip-label": {
      px: 1,
    },
  };
});

const ExpandButton = styled(IconButton)(({ theme, expanded }) => ({
  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shorter,
  }),
}));

const formatStatus = (status) => {
  return status?.replace(/-/g, " ");
};

const MetricScore = ({ value, label, icon, onClick, size = "medium" }) => {
  // Convert value from 0-1 to 0-100 scale
  const score = Math.round(value * 100);

  // Determine color based on score
  const getColor = (score) => {
    if (score >= 90) return "success";
    if (score >= 50) return "warning";
    return "error";
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: size === "small" ? 90 : 160,
          gap: 1,
        }}
      >
        <Box
          sx={{
            color: `${getColor(score)}.main`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {label}
          <IconButton size="small" onClick={onClick}>
            <InfoIcon fontSize="small" />
          </IconButton>
        </Typography>
      </Box>
      <Box sx={{ flex: 1 }}>
        <LinearProgress
          variant="determinate"
          value={score}
          color={getColor(score)}
          sx={{
            height: size === "small" ? 6 : 8,
            borderRadius: "4px",
          }}
        />
      </Box>
      <Typography
        variant={size === "small" ? "caption" : "body2"}
        fontWeight="bold"
        sx={{
          width: size === "small" ? 32 : 40,
          textAlign: "right",
          color: `${getColor(score)}.main`,
        }}
      >
        {score}%
      </Typography>
    </Box>
  );
};

const SiteCard = ({
  site,
  isTemplatesSite = false,
  isAdminTemplates = false,
  showAccountContext = false,
  accountName,
  onManage,
  onDelete,
  onEdit,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState();
  const [siteImage, setSiteImage] = useState(site.image || defaultTemplateUrl);

  const {
    id,
    siteName,
    siteType,
    description,
    accountId,
    status,
    url,
    slug,
    domain,
    lastUpdated,
    createdAt,
  } = site;

  const currentAccount = getLocalStorage(LOCAL_STORAGE_CURRENT_ACCOUNT);
  const currentUserID = currentAccount?.id || accountId;

  const webCheckResult =
    site["web_check_result_dev"] || site["web_check_result_external"] || {};

  // Calculate time since last update
  const getTimeSince = (timestamp) => {
    if (!timestamp) return "N/A";

    const now = new Date();
    const updateTime = new Date(timestamp);
    const diffMs = now - updateTime;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 1) {
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours < 1) {
        const diffMins = Math.floor(diffMs / (1000 * 60));
        return `${diffMins} minutes ago`;
      }
      return `${diffHours} hours ago`;
    } else if (diffDays === 1) {
      return "Yesterday";
    } else if (diffDays < 30) {
      return `${diffDays} days ago`;
    } else {
      return updateTime.toLocaleDateString();
    }
  };

  // Format domains for display
  const domainList = useMemo(() => {
    if (siteType === "Manual") {
      return [url];
    }
    if (status === "pending") return [];
    if (status === "pending-test" || status === "active") {
      return [`http://dev-${slug}.pantheonsite.io`];
    }
    if (status === "pending-production" || status === "test") {
      return [
        `http://dev-${slug}.pantheonsite.io`,
        `http://test-${slug}.pantheonsite.io`,
      ];
    }
    if (status === "production") {
      return [
        `http://dev-${slug}.pantheonsite.io`,
        `http://test-${slug}.pantheonsite.io`,
        `http://live-${slug}.pantheonsite.io`,
      ];
    }
    return [];
  }, [siteType, slug, status, url]);

  // Process lighthouse chart data
  const chartData = useMemo(() => {
    const categories = [];
    if (webCheckResult?.categories) {
      ["performance", "accessibility", "best-practices", "seo"].forEach(
        (key) => {
          const category = webCheckResult.categories[key];
          categories.push(category);
        }
      );
    }
    return categories;
  }, [webCheckResult?.categories]);

  // Handle clicking info icon for category details
  const handleClickInfo = useCallback(
    (category) => {
      const relatedAudits = category.auditRefs.map((audit) => audit.id);
      const failedAudits = [];
      const passedAudits = [];
      relatedAudits.forEach((audit) => {
        const auditDetail = webCheckResult.audits[audit];
        if (auditDetail?.score === 1) {
          passedAudits.push(auditDetail);
        }
        if (auditDetail?.score < 1) {
          failedAudits.push(auditDetail);
        }
      });
      setSelectedCategory({
        failedAudits,
        passedAudits,
        title: category.title,
      });
    },
    [webCheckResult?.audits]
  );

  // Handle card expansion toggle
  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  // Get icon based on category name
  const getCategoryIcon = (category) => {
    const name = category.title.toLowerCase();
    if (name.includes("performance")) return <SpeedIcon fontSize="small" />;
    if (name.includes("accessibility"))
      return <AccessibilityIcon fontSize="small" />;
    if (name.includes("best")) return <CheckCircleIcon fontSize="small" />;
    if (name.includes("seo")) return <LanguageIcon fontSize="small" />;
    return <InfoIcon fontSize="small" />;
  };

  return (
    <Card
      elevation={2}
      sx={{
        mb: 2,
        borderRadius: 2,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          boxShadow: 4,
          scale: 1.01,
        },
      }}
    >
      {/* Main Card Content */}
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {/* Site Image */}
          <Grid item xs={12} sm={3} md={2}>
            <Box
              sx={{
                height: 120,
                borderRadius: 1,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "background.neutral",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <img
                src={siteImage}
                alt={siteName}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "cover",
                  width: "100%",
                  height: "100%",
                }}
              />
            </Box>
          </Grid>

          {/* Site Information */}
          <Grid item xs={12} sm={9} md={4}>
            <Stack spacing={1}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="h5"
                  component={Link}
                  to={`/account/${currentUserID}/site-details/${id}`}
                  sx={{
                    textDecoration: "none",
                    color: "primary.main",
                    fontWeight: "medium",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  {siteName}
                </Typography>

                {!!status && (
                  <StatusChip
                    label={formatStatus(status)}
                    status={status}
                    size="small"
                  />
                )}

                {siteType === "Manual" && (
                  <Chip
                    label="External"
                    size="small"
                    sx={{
                      bgcolor: "grey.200",
                      color: "grey.700",
                      fontWeight: "medium",
                    }}
                  />
                )}
              </Box>

              {/* Collection Context Display */}
              {showAccountContext && accountName && (
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontWeight: 500,
                    mt: 0.5
                  }}
                >
                  <LanguageIcon sx={{ fontSize: 14 }} />
                  Collection: {accountName}
                </Typography>
              )}

              {description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    mb: 1,
                  }}
                >
                  {description}
                </Typography>
              )}

              {/* Platform/CMS Info */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <StorageIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  Platform: <strong>{siteType || "Unknown"}</strong>
                </Typography>
              </Box>

              {/* Timestamp Info */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <AccessTimeIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  Last updated:{" "}
                  <strong>{getTimeSince(lastUpdated || createdAt)}</strong>
                </Typography>
              </Box>

              {/* Domain/URL Info */}
              {domain && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <LanguageIcon fontSize="small" color="action" />
                  <Typography variant="body2" component="span">
                    <MuiLink
                      href={
                        domain.startsWith("http") ? domain : `https://${domain}`
                      }
                      target="_blank"
                      rel="noopener"
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      {domain}
                      <OpenInNewIcon fontSize="inherit" />
                    </MuiLink>
                  </Typography>
                </Box>
              )}
            </Stack>
          </Grid>

          {/* Performance Metrics */}
          <Grid item xs={12} md={4}>
            <Box sx={{ height: "100%" }}>
              {chartData.length > 0 ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Performance Metrics
                  </Typography>
                  {chartData.map((category) => (
                    <MetricScore
                      key={category.title}
                      label={category.title}
                      value={category.score}
                      icon={getCategoryIcon(category)}
                      onClick={() => handleClickInfo(category)}
                    />
                  ))}
                </Box>
              ) : (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    bgcolor: "grey.100",
                    borderRadius: 1,
                    p: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                  >
                    No performance data available
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12} md={2}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                height: "100%",
                justifyContent: "center",
              }}
            >
              {!isTemplatesSite && (
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={() => onManage(site.id, accountId)}
                  sx={{ borderRadius: 1 }}
                >
                  Manage Site
                </Button>
              )}

              {!isTemplatesSite && (
                <Button
                  variant="outlined"
                  component={Link}
                  to={`/account/${currentUserID}/site-details/${id}`}
                  fullWidth
                  sx={{ borderRadius: 1 }}
                >
                  View Details
                </Button>
              )}

              {isAdminTemplates && (
                <ButtonGroup variant="outlined" fullWidth sx={{ mt: 1 }}>
                  <Button
                    color="error"
                    onClick={() => onDelete(site)}
                    startIcon={<DeleteIcon />}
                  >
                    Delete
                  </Button>
                  <Button
                    color="primary"
                    onClick={() => onEdit(site.id)}
                    startIcon={<EditIcon />}
                  >
                    Edit
                  </Button>
                </ButtonGroup>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Expandable Section */}
      {(domainList.length > 0 || chartData.length > 3) && (
        <>
          <Divider />
          <Box
            sx={{
              p: 1,
              display: "flex",
              justifyContent: "center",
              bgcolor: "grey.50",
            }}
          >
            <ExpandButton
              expanded={expanded}
              onClick={handleExpandClick}
              aria-expanded={expanded}
              aria-label="show more"
              size="small"
            >
              <ExpandMoreIcon />
            </ExpandButton>
          </Box>
        </>
      )}

      {/* Expanded Content */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ px: 2, bgcolor: "grey.50" }}>
          {domainList.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                All Environments
              </Typography>
              <Stack spacing={1}>
                {domainList.map((domain, index) => (
                  <Box
                    key={index}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <DevicesIcon fontSize="small" color="action" />
                    <MuiLink
                      href={domain}
                      target="_blank"
                      rel="noopener"
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      {domain}
                      <OpenInNewIcon fontSize="inherit" />
                    </MuiLink>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      </Collapse>

      {/* Performance Details Modal */}
      {!!selectedCategory && (
        <PerformanceDetailsModal
          open={!!selectedCategory}
          handleClose={() => setSelectedCategory(undefined)}
          title={selectedCategory.title}
          failedAudits={selectedCategory.failedAudits}
          passedAudits={selectedCategory.passedAudits}
        />
      )}
    </Card>
  );
};

export default SiteCard;
